import { useEffect, useState } from 'react'

import { PagedListRequest } from 'domain/types/global/pagedListRequest';
import { useTranslation } from 'react-i18next';
import { CampanhaPedidoResgateResponse } from 'domain/types/_carteira/campanhaPedidoResgate/campanhaPedidoResgateResponse';
import { RootStateCampanha } from '../../../../store/campanhaReducer';
import { GetSolicitadosByCampanha, ResetSolicitadosByCampanha } from 'domain/store/_carteira/campanhaPedidoResgate/actions/campanhaPedidoResgateAction';
import moment from 'moment';
import 'moment/dist/locale/pt-br'

import { useSidebar } from 'base/src/utils/context/sidebarContext';
import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { CAMPANHA_PEDIDORESGATE_GET_BY_CAMPANHA } from 'domain/types/_carteira/campanhaPedidoResgate/campanhaPedidoResgate';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Avatar,
  IconButton,
  SelectChangeEvent,
  Chip
} from '@mui/material';
import { Icon } from '@iconify/react';
import { formatDate } from 'base/src/utils/stringUtil';
import { formatToBRL } from 'base/src/utils/mathUtil';
import { Tag } from 'base/src/components/Tag';
import { Button } from 'base/src/components/Button';
import { Modal, ModalContent } from 'base/src/components/Modal';
import NoData from 'base/src/components/NoData';
import AprovarPedidoDetalhes from '../aprovarPedidoDetalhes/aprovarPedidoDetalhes';
import FilterDrawer from './components/filterDrawer/filterDrawer';
import { useTheme } from 'base/src/utils/context/themeContext';

const connector = connectUtil(
  (state: RootStateCampanha) => ({
    CampanhaPedidoResgate: state.CampanhaPedidoResgateReducer.CampanhaPedidoResgateByCampanha,
    CampanhasDisponiveis: state.CampanhasReducer?.campanhasAll,
    mudancaStatus: state.AprovarPedidoReducer.mudancaStatus,
    loading: state.BaseReducer.Loading
  }),
  { GetSolicitadosByCampanha, ResetSolicitadosByCampanha }
);

export interface PagedResgatesProps extends PropsFromRedux<typeof connector> {
  take: number;
  campanhaId: number;
}

function PagedResgates(props: PagedResgatesProps) {
  const { theme } = useTheme();
  const [request, setRequest] = useState<PagedListRequest<CampanhaPedidoResgateResponse>>({
    skip: 0,
    take: props.take,
    search: '',
    filter: [{
      Values: ['1'], // "Solicitado" filter
      Id: 0,
      Name: "Status"
    }],
    data: {} as CampanhaPedidoResgateResponse
  });

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>(['1']); // Start with "Solicitado" selected
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CampanhaPedidoResgateResponse | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { collapsed } = useSidebar();
  const { t } = useTranslation();

  moment.locale("pt-br");

  // Status options
  const statusOptions = [
    { id: '1', nome: 'Solicitado' },
    { id: '2', nome: 'Aprovado' },
    { id: '3', nome: 'Não Aprovada' },
    { id: '4', nome: 'Pago/Entregue' },
    { id: '5', nome: 'Em Espera' },
    { id: '6', nome: 'Cancelado' }
  ];

  // Get status variant for Tag component
  function getStatusVariant(statusId: number) {
    if (statusId === 1 || statusId === 5) return 'warning';
    if (statusId === 2 || statusId === 4) return 'positive';
    if (statusId === 3 || statusId === 6) return 'danger';
    return 'positive';
  }

  // Handle pagination change
  function handlePageChange(_: unknown, newPage: number) {
    const newRequest = {
      ...request,
      skip: newPage * request.take
    };
    setRequest(newRequest);

    // Reset data to clear previous page data
    props.ResetSolicitadosByCampanha(newRequest, props.take);

    // Load new page data
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Handle rows per page change
  function handleRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newTake = parseInt(event.target.value, 10);
    const newRequest = {
      ...request,
      take: newTake,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous data
    props.ResetSolicitadosByCampanha(newRequest, newTake);

    // Load new data
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Handle search
  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    const searchVal = event.target.value;
    setSearchValue(searchVal);

    const newRequest = {
      ...request,
      search: searchVal,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous results
    props.ResetSolicitadosByCampanha(newRequest, props.take);

    // Load new filtered data
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  function handleStatusFilterChange(event: SelectChangeEvent<string[]>) {
    const values = event.target.value as string[];
    setStatusFilter(values);

    const filterArray = values.length > 0 ? [{
      Values: values,
      Id: 0,
      Name: "Status"
    }] : [];

    const newRequest = {
      ...request,
      filter: filterArray,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosByCampanha(newRequest, props.take);

    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Handle modal
  function handleOpenModal(item: CampanhaPedidoResgateResponse) {
    setSelectedItem(item);
    setIsModalOpen(true);
  }

  function handleCloseModal() {
    setTimeout(() => {
      setIsModalOpen(false);
      setSelectedItem(null);

      // Refresh data after modal closes to ensure we have the latest status
      props.ResetSolicitadosByCampanha(request, props.take);
      props.GetSolicitadosByCampanha([props.campanhaId], request, t);
    }, 50);
  }

  function handleOpenDrawer() {
    setIsDrawerOpen(true);
  }

  function handleCloseDrawer() {
    setIsDrawerOpen(false);
  }

  // Handle apply filters (close drawer after applying)
  function handleApplyFilters() {
    setIsDrawerOpen(false);
  }

  function handleClearFilters() {
    setSearchValue('');
    setStatusFilter([]);

    // Reset request to initial state
    const newRequest = {
      ...request,
      search: '',
      filter: [],
      skip: 0
    };
    setRequest(newRequest);

    // Reset data and load fresh data without filters
    props.ResetSolicitadosByCampanha(newRequest, props.take);
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Format pagination label
  function formatPaginationLabel({ from, to, count }: { from: number; to: number; count: number }) {
    return `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`;
  }

  // Reset data when status changes and reload fresh data
  useEffect(() => {
    if (props.mudancaStatus && Object.keys(props.mudancaStatus).length > 0) {
      // Reset data first
      props.ResetSolicitadosByCampanha(request, props.take);

      // Then reload with fresh data
      props.GetSolicitadosByCampanha([props.campanhaId], request, t);
    }
  }, [props.mudancaStatus]);

  // Load initial data
  useEffect(() => {
    props.GetSolicitadosByCampanha([props.campanhaId], request, t);
  }, [props.campanhaId]);

  // Get data from state
  const data = props.CampanhaPedidoResgate?.Result || [];
  const totalCount = props.CampanhaPedidoResgate?.TotalCount || 0;
  const isLoading = props.loading.some(l => l.Name === CAMPANHA_PEDIDORESGATE_GET_BY_CAMPANHA);

  const currentPage = Math.floor(request.skip / request.take);

  return (
    <Box sx={{
      '& @keyframes fadeIn': {
        '0%': {
          opacity: 0,
          transform: 'translateY(10px)'
        },
        '100%': {
          opacity: 1,
          transform: 'translateY(0)'
        }
      }
    }}>
      {/* Action Bar: SearchBar - Filtros - PagarPedido */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}>
        {/* Search Bar */}
        <TextField
          label="Pesquisar por usuário"
          placeholder="Digite o nome do usuário"
          value={searchValue}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{
            minWidth: 300,
            flex: 1,
            maxWidth: 400,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            }
          }}
          InputProps={{
            startAdornment: (
              <Icon
                icon="mdi:magnify"
                style={{
                  marginRight: 8,
                  color: '#9CA3AF',
                  width: 20,
                  height: 20
                }}
              />
            ),
          }}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            size="md"
            variant="secondary"
            iconName="mdi:filter-variant"
            onClick={handleOpenDrawer}
          >
            Filtros
          </Button>

        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Data</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Usuário</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Descrição</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Chave PIX</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'grey.700' }}>Valor</TableCell>
              <TableCell width={50} sx={{ fontWeight: 600, color: 'grey.700' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 8, border: 'none' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography variant="body2" color="text.secondary">
                      Carregando dados...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, border: 'none' }}>
                  <NoData />
                </TableCell>
              </TableRow>
            ) : (
              data.map((item) => {
                function handleModalClick() {
                  handleOpenModal(item);
                }

                return (
                  <TableRow
                    key={item.CampanhaPedidoResgateID}
                    hover
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                        cursor: 'pointer',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      },
                      '&:nth-of-type(even)': {
                        bgcolor: 'grey.25'
                      },
                      transition: 'all 0.2s ease-in-out',
                      animation: 'fadeIn 0.3s ease-in-out'
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                          Data do pedido
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.900' }}>
                          {formatDate(item.HoraPedido)}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={item._Usuario.ImagemUrl}
                          alt={item._Usuario.NomeCompleto}
                          sx={{
                            width: 40,
                            height: 40,
                            border: '2px solid',
                            borderColor: 'grey.200'
                          }}
                        >
                          {item._Usuario.NomeCompleto?.substring(0, 2) || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.900' }}>
                            {item._Usuario.NomeCompleto}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'grey.600' }}>
                            {item.Premiacao.Campanha.Titulo}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.900' }}>
                          {item.Premiacao.Descricao}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                          Prêmio solicitado
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          color: theme.colors.primary,
                          fontWeight: 600
                        }}
                      >
                        #{item.CampanhaPedidoResgateID}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={item.Premiacao.PremiacaoTipo.Titulo}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          color: theme.colors.primary,
                          borderColor: theme.colors.primary,
                          backgroundColor: 'info.50'
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            color: 'grey.800',
                            fontWeight: 500,
                            fontSize: '0.875rem'
                          }}
                        >
                          {item._Usuario.ChavePix || 'Não informado'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                          Chave PIX
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      {item.CampanhaPedidoResgateStatus && (
                        <Tag
                          full
                          variant={getStatusVariant(item.CampanhaPedidoResgateStatusID)}
                        >
                          {item.CampanhaPedidoResgateStatus.Titulo}
                        </Tag>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: 'success.main',
                            fontWeight: 700,
                            fontSize: '1.1rem'
                          }}
                        >
                          {formatToBRL((item.Premiacao.ValorMonetario ? Number(item.Premiacao.ValorMonetario) : Number(item.Premiacao.Valor)) * Number(item.Qtde))}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                          Valor total
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={handleModalClick}
                        sx={{
                          color: theme.colors.primary,
                          backgroundColor: 'primary.50',
                          borderRadius: 2,
                          '&:hover': {
                            color: 'primary.dark',
                            backgroundColor: 'primary.100',
                            transform: 'scale(1.05)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <Icon icon='mdi:dots-vertical' width={22} height={22} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box sx={{ backgroundColor: 'grey.25', borderTop: '1px solid', borderColor: 'grey.200' }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage}
            onPageChange={handlePageChange}
            rowsPerPage={request.take}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={formatPaginationLabel}
            sx={{
              '& .MuiTablePagination-toolbar': {
                paddingX: 3,
                paddingY: 2
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: 'grey.700',
                fontWeight: 500
              },
              '& .MuiTablePagination-actions button': {
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.50'
                }
              }
            }}
          />
        </Box>
      </TableContainer>

      {/* Modal */}
      <Modal open={isModalOpen} onOpenChange={setIsModalOpen}>
        <ModalContent affectedBySidebar={collapsed}>
          {selectedItem && (
            <AprovarPedidoDetalhes
              campanhaPedidoResgate={selectedItem}
              onClose={handleCloseModal}
            />
          )}
        </ModalContent>
      </Modal>

      {/* Filters Drawer */}
      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        statusOptions={statusOptions}
      />
    </Box>
  );
}
export default connector(PagedResgates);